/**
 * 权限管理状态
 * 处理用户权限、角色和路由权限
 * @author AI Assistant
 * @date 2024-01-01
 */

import { defineStore } from 'pinia'
import { ref, computed, type ComputedRef, type Ref } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from './user'
import { constantRoutes } from '@/router'
import type { PermissionConfig } from '@/types/permission'

// 权限相关类型定义
export interface IPermission {
  id: string
  name: string
  code: string
  type: 'menu' | 'button' | 'api'
  resource?: string
  action?: string
}

export interface IRole {
  id: string
  name: string
  code: string
  permissions: IPermission[]
  description?: string
}

export interface IUserPermissions {
  routes: string[]
  buttons: string[]
  apis: string[]
}

export interface PermissionStore {
  routes: Ref<RouteRecordRaw[]>
  addRoutes: Ref<RouteRecordRaw[]>
  permissions: Ref<IPermission[]>
  roles: Ref<IRole[]>
  userPermissions: Ref<IUserPermissions>
  loading: Ref<boolean>
  error: Ref<string | null>
  apiPermissionMap: Ref<Record<string, string>>
  permissionConfigMap: Ref<Record<string, PermissionConfig>>
  hasPermission: ComputedRef<(permission: string) => boolean>
  hasRole: ComputedRef<(roleCode: string) => boolean>
  hasAnyRole: ComputedRef<(roleCodes: string[]) => boolean>
  hasAllRoles: ComputedRef<(roleCodes: string[]) => boolean>
  generateRoutes: () => Promise<RouteRecordRaw[]>
  fetchUserPermissions: () => Promise<IUserPermissions>
  fetchPermissions: () => Promise<IPermission[]>
  fetchRoles: () => Promise<IRole[]>
  resetPermissions: () => void
  hasRoutePermission: (route: RouteRecordRaw) => boolean
  checkButtonPermission: (permission: string) => boolean
  checkApiPermission: (api: string) => boolean
  initPermissionMap: (configs: PermissionConfig[]) => void
  getPermissionByApi: (apiPath: string) => string | undefined
  hasApiPermission: (apiPath: string) => boolean
}

/**
 * 权限管理 Store
 */
export const usePermissionStore = defineStore('permission', (): PermissionStore => {
  // 状态定义
  const routes = ref<RouteRecordRaw[]>([])
  const addRoutes = ref<RouteRecordRaw[]>([])
  const permissions = ref<IPermission[]>([])
  const roles = ref<IRole[]>([])
  const userPermissions = ref<IUserPermissions>({
    routes: [],
    buttons: [],
    apis: []
  })
  const loading = ref(false)
  const error = ref<string | null>(null)

  // API权限映射表
  const apiPermissionMap = ref<Record<string, string>>({})
  const permissionConfigMap = ref<Record<string, PermissionConfig>>({})

  // 计算属性
  const hasPermission = computed(() => {
    return (permission: string): boolean => {
      if (!permission) return true

      const userStore = useUserStore()
      if (userStore.isAdmin) return true

      return userPermissions.value.buttons.includes(permission) ||
             userPermissions.value.apis.includes(permission)
    }
  })

  const hasRole = computed(() => {
    return (roleCode: string): boolean => {
      if (!roleCode) return true

      const userStore = useUserStore()
      return userStore.roles.includes(roleCode)
    }
  })

  const hasAnyRole = computed(() => {
    return (roleCodes: string[]): boolean => {
      if (!roleCodes || roleCodes.length === 0) return true

      const userStore = useUserStore()
      return roleCodes.some(code => userStore.roles.includes(code))
    }
  })

  const hasAllRoles = computed(() => {
    return (roleCodes: string[]): boolean => {
      if (!roleCodes || roleCodes.length === 0) return true

      const userStore = useUserStore()
      return roleCodes.every(code => userStore.roles.includes(code))
    }
  })

  /**
   * 检查路由权限
   */
  const hasRoutePermission = (route: RouteRecordRaw): boolean => {

    // 如果路由标记为不需要权限验证，直接返回true
    if (route.meta?.noAuth) {
      return true
    }

    const userStore = useUserStore()

    // 如果是管理员，拥有所有权限 - 这里应该是最优先的判断
    if (userStore.isAdmin) {
      return true
    }

    // 如果用户拥有所有权限（*），直接通过
    if (userPermissions.value.routes.includes('*')) {
      return true
    }

    const requiredRoles = route.meta?.roles as string[] | undefined
    const requiredPermissions = route.meta?.permissions as string[] | undefined

    // 如果没有设置任何权限要求，默认允许访问
    if (!requiredRoles && !requiredPermissions) {
      return true
    }

    let hasRoleAccess = true
    let hasPermissionAccess = true

    // 检查角色权限
    if (requiredRoles && requiredRoles.length > 0) {
      hasRoleAccess = requiredRoles.some(role =>
        userStore.userRoles.includes(role)
      )
    }

    // 检查权限
    if (requiredPermissions && requiredPermissions.length > 0) {
      hasPermissionAccess = requiredPermissions.some(permission =>
        userPermissions.value.routes.includes(permission)
      )
    }

    // 如果同时设置了角色和权限要求，需要同时满足
    // 如果只设置了其中一个，只需要满足设置的那个
    const finalResult = hasRoleAccess && hasPermissionAccess

    return finalResult
  }

  /**
   * 过滤异步路由
   */
  const filterAsyncRoutes = (routes: RouteRecordRaw[]): RouteRecordRaw[] => {
    const filteredRoutes: RouteRecordRaw[] = []

    routes.forEach(route => {
      const tmp = { ...route }
      if (hasRoutePermission(tmp)) {
        if (tmp.children) {
          tmp.children = filterAsyncRoutes(tmp.children)
        }
        filteredRoutes.push(tmp)
      }
    })

    return filteredRoutes
  }

  /**
 * 生成路由
 */
  const generateRoutes = async (): Promise<RouteRecordRaw[]> => {
    try {
      loading.value = true
      error.value = null

      // 获取用户权限
      await fetchUserPermissions()

      // 从模块配置加载器获取路由
      const { moduleLoader } = await import('@/core/module-loader')
      await moduleLoader.loadModules()

      // 获取用户角色
      const userStore = useUserStore()
      const userRoles = userStore.roles // roles已经是字符串数组，不需要map取code

      // 根据权限过滤路由
      const { routes: accessedRoutes } = moduleLoader.filterByPermission(
        userPermissions.value.routes,
        userRoles
      )

      // 合并路由
      const allRoutes = constantRoutes.concat(accessedRoutes)

      routes.value = allRoutes
      addRoutes.value = accessedRoutes

      return accessedRoutes
    } catch (err) {
      console.error('❌ 生成权限路由失败:', err)
      error.value = err instanceof Error ? err.message : '生成路由失败'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 获取用户权限
   */
  const fetchUserPermissions = async (): Promise<IUserPermissions> => {
    try {
      loading.value = true

      const userStore = useUserStore()

      // 管理员拥有所有权限
      if (userStore.isAdmin) {
        // 模拟所有路由权限
        userPermissions.value = {
          routes: ['*'], // 所有路由
          buttons: ['*'], // 所有按钮
          apis: ['*'] // 所有API
        }
        return userPermissions.value
      }

      // 模拟从后端获取权限数据
      await new Promise(resolve => setTimeout(resolve, 500))

      // 根据用户角色获取权限
      const userRoles = userStore.roles

      // 从模块配置加载器获取权限
      const { getPermissionsByRoles } = await import('@/core/module-loader')
      const { routePermissions, buttonPermissions, apiPermissions } = getPermissionsByRoles(
        userRoles
      )

      userPermissions.value = {
        routes: routePermissions,
        buttons: buttonPermissions,
        apis: apiPermissions
      }

      return userPermissions.value
    } catch (err) {
      error.value = err instanceof Error ? err.message : '获取权限失败'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
 * 获取所有权限
 */
  const fetchPermissions = async (): Promise<IPermission[]> => {
    try {
      loading.value = true

      // 从模块配置加载器获取权限
      const { getPermissions } = await import('@/core/module-loader')
      const modulePermissions = getPermissions()

      // 转换为权限对象
      const allPermissions: IPermission[] = modulePermissions.map((p, index) => ({
        id: String(index + 1),
        name: p.name,
        code: p.code,
        type: p.type,
        resource: p.code.split(':')[1] || '',
        action: p.code.split(':')[2] || ''
      }))

      permissions.value = allPermissions
      return allPermissions
    } catch (err) {
      error.value = err instanceof Error ? err.message : '获取权限列表失败'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 获取所有角色
   */
  const fetchRoles = async (): Promise<IRole[]> => {
    try {
      loading.value = true

      // 从模块配置加载器获取角色
      const { getRoles } = await import('@/core/module-loader')
      const moduleRoles = getRoles()

      // 转换为角色对象
      const allRoles: IRole[] = [
        {
          id: '1',
          name: '超级管理员',
          code: 'admin',
          permissions: [],
          description: '系统超级管理员，拥有所有权限'
        },
        ...moduleRoles.map((r, index) => ({
          id: String(index + 2),
          name: r.name,
          code: r.code,
          permissions: [],
          description: r.description || `${r.name}角色`
        }))
      ]

      roles.value = allRoles
      return allRoles
    } catch (err) {
      error.value = err instanceof Error ? err.message : '获取角色列表失败'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 重置权限状态
   */
  const resetPermissions = (): void => {
    routes.value = []
    addRoutes.value = []
    permissions.value = []
    roles.value = []
    userPermissions.value = {
      routes: [],
      buttons: [],
      apis: []
    }
    error.value = null
  }

  /**
   * 检查按钮权限
   */
  const checkButtonPermission = (permission: string): boolean => {
    return hasPermission.value(permission)
  }

  /**
   * 检查API权限
   */
  const checkApiPermission = (api: string): boolean => {
    if (!api) return true

    const userStore = useUserStore()
    if (userStore.isAdmin) return true

    return userPermissions.value.apis.some(userApi => {
      if (userApi === '*') return true
      if (userApi.endsWith('/*')) {
        const prefix = userApi.slice(0, -2)
        return api.startsWith(prefix)
      }
      return userApi === api
    })
  }

  /**
   * 初始化权限配置映射
   */
  const initPermissionMap = (configs: PermissionConfig[]) => {
    // 构建权限配置映射
    permissionConfigMap.value = configs.reduce((map, config) => {
      map[config.code] = config
      return map
    }, {} as Record<string, PermissionConfig>)

    // 构建API权限映射
    apiPermissionMap.value = configs.reduce((map, config) => {
      if (config.apiPath) {
        map[config.apiPath] = config.code
      }
      return map
    }, {} as Record<string, string>)
  }

  /**
   * 根据API路径获取权限代码
   */
  const getPermissionByApi = (apiPath: string): string | undefined => {
    // 精确匹配
    if (apiPermissionMap.value[apiPath]) {
      return apiPermissionMap.value[apiPath]
    }

    // 模糊匹配（处理路径参数）
    for (const [path, permission] of Object.entries(apiPermissionMap.value)) {
      if (matchApiPath(path, apiPath)) {
        return permission
      }
    }

    return undefined
  }

  /**
   * 检查API路径权限
   */
  const hasApiPermission = (apiPath: string): boolean => {
    const permissionCode = getPermissionByApi(apiPath)

    if (!permissionCode) {
      return true // 没有配置权限的API默认允许访问
    }

    return hasPermission.value(permissionCode)
  }

  return {
    // 状态
    routes,
    addRoutes,
    permissions,
    roles,
    userPermissions,
    loading,
    error,
    apiPermissionMap,
    permissionConfigMap,

    // 计算属性
    hasPermission,
    hasRole,
    hasAnyRole,
    hasAllRoles,

    // 方法
    generateRoutes,
    fetchUserPermissions,
    fetchPermissions,
    fetchRoles,
    resetPermissions,
    hasRoutePermission,
    checkButtonPermission,
    checkApiPermission,
    initPermissionMap,
    getPermissionByApi,
    hasApiPermission
  }
})

/**
 * 权限检查工具函数
 */
export const checkPermission = (permission: string | string[]): boolean => {
  const permissionStore = usePermissionStore()

  if (Array.isArray(permission)) {
    return permission.some(p => permissionStore.hasPermission(p))
  }

  return permissionStore.hasPermission(permission)
}

/**
 * 角色检查工具函数
 */
export const checkRole = (role: string | string[]): boolean => {
  const permissionStore = usePermissionStore()

  if (Array.isArray(role)) {
    return permissionStore.hasAnyRole(role)
  }

  return permissionStore.hasRole(role)
}

/**
 * API权限检查工具函数
 */
export const checkApiPermission = (api: string): boolean => {
  const permissionStore = usePermissionStore()
  return permissionStore.checkApiPermission(api)
}

/**
 * API路径匹配函数
 * 支持路径参数匹配，如 /api/users/:id 匹配 /api/users/123
 */
const matchApiPath = (pattern: string, path: string): boolean => {
  // 如果完全相同，直接返回true
  if (pattern === path) return true

  // 将路径参数模式转换为正则表达式
  const regexPattern = pattern
    .replace(/:[^/]+/g, '[^/]+') // :id -> [^/]+
    .replace(/\*/g, '.*') // * -> .*
    .replace(/\//g, '\\/') // / -> \/

  const regex = new RegExp(`^${regexPattern}$`)
  return regex.test(path)
}
